<template>
    <b-modal
        id="add-skilltree"
        :title="$t('skill_tree.add_set')"
        @ok="onOk"
        @show="onShow"
        cancel-variant="outline-primary"
        :cancel-title="$t('common.cancel')"
        modal-class="apprentice-default-modal">
        <div v-if="!defaultSkillTree && !newSkillTree">
            <div class="row">
                <div class="col-6 new-set">
                    <div class="add-new-set apprentice-simple-card">
                        <div class="new-set-icon icon">
                            <i class="btn btn-primary-icon-only btn-add-layer"></i>
                        </div>
                        <div class="new-set-content content">
                            <h5 class="title">{{ $t('skill_tree.new_set') }}</h5>
                            <div class="description">
                                {{ $t('skill_tree.start_creating_a_completely_new_custom_set_of_skills') }}
                            </div>
                        </div>
                        <a
                            class="clickable"
                            href=""
                            @click.prevent.stop="setNewSkillTree"></a>
                    </div>
                </div>

                <div class="col-6 existing-set">
                    <div class="new-existing-set apprentice-simple-card">
                        <div class="new-existing-set-icon icon">
                            <i class="btn btn-primary-icon-only btn-books"></i>
                        </div>
                        <div class="new-set-content content">
                            <h5 class="title">{{ $t('skill_tree.standard_set') }}</h5>
                            <div class="description">
                                {{
                                    $t(
                                        'skill_tree.choose_from_a_standard_set_that_has_already_been_completed_and_can_then_be_adjusted_at_your_own_discretion',
                                    )
                                }}
                            </div>
                        </div>
                        <a
                            class="clickable"
                            href=""
                            @click.prevent.stop="setDefaultSkillTree"></a>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="defaultSkillTree">
            <div v-if="defaultSkillTrees === null && isLoaded">
                <b-alert
                    show
                    variant="danger">
                    {{ $t('skill_tree.no_standard_sets_found') }}
                </b-alert>
            </div>
            <div v-else>
                <div v-if="defaultSkillTrees">
                    <b-form-group :label="$t('skill_tree.skills')">
                        <design-2021-apprentice-multiselect-component
                            class="apprentice-multiselect"
                            :options="defaultSkillTrees"
                            label="name"
                            track-by="name"
                            :placeholder="$t('skill_tree.select_a_set')"
                            :multiple-select="false"
                            @input="copyDefaultTree($event)">
                        </design-2021-apprentice-multiselect-component>
                    </b-form-group>

                    <b-form-group
                        v-if="lasGroupsIn.length"
                        :label="$t('skill_tree.lesson_groups')"
                        label-for="skill-las-group-select">
                        <template v-slot:label>{{ $t('skill_tree.lesson_groups') }}</template>

                        <design-2021-apprentice-multiselect-component
                            id="skill-las-group-select"
                            @input="addLasGroup($event)"
                            :selected-in="selectedLasGroups"
                            value-field="id"
                            label="name"
                            :options="lasGroupsIn"
                            :multiple-select="true"></design-2021-apprentice-multiselect-component>
                    </b-form-group>

                    <b-form-group
                        v-if="lasMainGroupsIn.length"
                        :label="`${$t('skill_tree.main_groups')}:`"
                        label-for="skill-las-main-group-select">
                        <template v-slot:label> {{ $t('skill_tree.main_groups') }}: </template>

                        <design-2021-apprentice-multiselect-component
                            id="skill-level-select"
                            @input="addLasMainGroup($event)"
                            :selected-in="selectedLasMainGroups"
                            value-field="id"
                            label="name"
                            :options="lasMainGroupsIn"
                            :multiple-select="true"></design-2021-apprentice-multiselect-component>
                    </b-form-group>
                </div>
            </div>
        </div>

        <div v-if="newSkillTree">
            <div class="add-skill-tree">
                <div v-if="errorName">
                    <b-alert
                        show
                        variant="danger"
                        >{{ $t('validation.name_mandatory') }}
                    </b-alert>
                </div>
                <b-form-group
                    :label="$t('common.name')"
                    label-for="skill-name-input">
                    <template v-slot:label>{{ $t('common.name') }} <span class="required">*</span></template>
                    <b-form-input
                        id="skill-name-input"
                        v-model="name"
                        required
                        :placeholder="$t('common.name')">
                    </b-form-input>
                </b-form-group>

                <b-form-group
                    v-if="lasGroupsIn.length"
                    :label="$t('skill_tree.lesson_groups')"
                    label-for="skill-las-group-select">
                    <template v-slot:label>
                        {{ $t('skill_tree.lesson_groups') }}
                    </template>

                    <design-2021-apprentice-multiselect-component
                        id="skill-las-group-select"
                        @input="addLasGroup($event)"
                        :selected-in="selectedLasGroups"
                        value-field="id"
                        label="name"
                        :options="lasGroupsIn"
                        :multiple-select="true"></design-2021-apprentice-multiselect-component>
                </b-form-group>

                <b-form-group
                    v-if="lasMainGroupsIn.length"
                    :label="$t('skill_tree.main_groups')"
                    label-for="skill-las-main-group-select">
                    <template v-slot:label>
                        {{ $t('skill_tree.main_groups') }}
                    </template>

                    <design-2021-apprentice-multiselect-component
                        id="skill-level-select"
                        @input="addLasMainGroup($event)"
                        :selected-in="selectedLasMainGroups"
                        value-field="id"
                        label="name"
                        :options="lasMainGroupsIn"
                        :multiple-select="true"></design-2021-apprentice-multiselect-component>
                </b-form-group>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { ords } from '../../../app';

const defaultSkillTreesOrds = ords.use('default_skill_trees');

export default {
    name: 'design-2021-skilltree-add-modal-component',
    props: ['lasGroupsIn', 'lasMainGroupsIn'],
    data() {
        return {
            name: '',
            errorName: false,
            levelOptions: [],
            defaultSkillTree: false,
            newSkillTree: false,
            defaultSkillTrees: null,
            selectedLasGroups: [],
            selectedLasMainGroups: [],
            defaultTree: null,
            isLoaded: false,
        };
    },
    methods: {
        copyDefaultTree(defaultTree) {
            this.defaultTree = defaultTree;
        },

        setNewSkillTree() {
            this.newSkillTree = true;
        },

        setDefaultSkillTree() {
            this.axios.get(this.routes.getDefaultSkillTrees()).then(
                (response) => {
                    if (response.data.length !== 0) {
                        defaultSkillTreesOrds.add(response.data);

                        ords.use('default_skill_trees')
                            .with('skill_tree', (skillTreeCallback) => {
                                skillTreeCallback.with(
                                    'school_education_level_skill_trees',
                                    (schoolEducationLevelSkillTreesCallback) => [
                                        schoolEducationLevelSkillTreesCallback.with('school_education_levels'),
                                    ],
                                );
                            })
                            .get()
                            .subscribe((skillTrees) => {
                                this.defaultSkillTrees = skillTrees;
                            });

                        this.defaultSkillTrees.forEach((tree) => {
                            tree.name = tree.skill_tree.name;
                        });
                    }

                    this.isLoaded = true;
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );

            this.defaultSkillTree = true;
        },

        addLasGroup(event) {
            this.selectedLasGroups = event;
        },

        addLasMainGroup(event) {
            this.selectedLasMainGroups = event;
        },

        onOk(evt) {
            if (this.newSkillTree) {
                this.errorName = this.name === '';

                if (this.errorName) {
                    evt.preventDefault();
                } else {
                    this.alert.saving();

                    let data = {
                        name: this.name,
                        lasGroups: this.selectedLasGroups,
                        lasMainGroups: this.selectedLasMainGroups,
                    };

                    this.axios.post(this.routes.skillTreeStore(), data).then(
                        (response) => {
                            ords.use('skillTree').add(response.data);

                            this.alert.success();

                                window.location.href = this.routes.skillTreeEdit(response.data.id);
                        },
                        (error) => {
                            this.alert.ajaxError(error);
                        },
                    );
                }
            }

            if (this.defaultSkillTree) {
                this.alert.saving();

                let data = {
                    lasGroups: this.selectedLasGroups,
                    lasMainGroups: this.selectedLasMainGroups,
                };

                this.axios.post(this.routes.defaultSkillTreeCopy(this.defaultTree.id), data).then(
                    (response) => {
                        ords.use('skillTree').add(response.data);

                        this.alert.success();
                    },
                    (error) => {
                        this.alert.ajaxError(error);
                    },
                );
            }
        },

        onShow() {
            this.name = '';
            this.selectedLasGroups = [];
            this.selectedLasMainGroups = [];
            this.levelOptions = [];
            this.defaultSkillTree = false;
            this.newSkillTree = false;
            this.defaultTree = false;
        },
    },
};
</script>

<style scoped lang="scss">
.new-set,
.existing-set {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}

.add-new-set,
.new-existing-set {
    height: 100%;
    position: relative;
    background-color: $secondary-background-color;

    .icon {
        margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');

        i {
            font-size: 6rem;
        }
    }

    .content {
        .title {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        .description {
            color: $text-muted-color;
        }
    }

    &:hover {
        cursor: pointer;
        box-shadow: 1px 0px 4px $border-color;

        .icon {
            i {
                color: var(--primaryLightest);
            }
        }
    }
}

.selected-skill-tree-previews {
    .selected-skill {
        display: flex;
        flex-direction: column;
        flex-wrap: wrap;
        flex: 1;
        margin-left: -#{map-get($grid-gutter-spacing, 'twenty-one')};
        margin-right: -#{map-get($grid-gutter-spacing, 'twenty-one')};
        padding: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'twenty-one');

        .title {
            display: inline-flex;
            flex-grow: 1;
            font-size: 1.3125rem;
            font-weight: $font-weight-bold;
            color: $primary;

            .icon {
                margin-right: map-get($grid-gutter-spacing, 'eight');
            }
        }

        .school-years {
            .school-year {
                padding: map-get($grid-gutter-spacing, 'eight') map-get($grid-gutter-spacing, 'twenty-one');
                border-bottom: 3px dotted $border-color;

                label {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 0;
                }
            }
        }

        .btn-remove-skill {
            font-size: $default-font-size;
            margin-top: map-get($grid-gutter-spacing, 'twenty-one');
            display: flex;
            align-items: center;
            flex-direction: row;
            align-self: flex-end;

            i {
                margin-right: map-get($grid-gutter-spacing, 'eight');
            }
        }

        &:nth-child(even) {
            background-color: $secondary-background-color;
        }
    }
}
</style>
